import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";

import tajawalFont from "../assets/font/Tajawal-Regular.ttf";

Font.register({
  family: "Tajawal",
  fonts: [
    {
      src: tajawalFont,
    },
  ],
});

// Create styles using the transaction template styles
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#ffffff",
    padding: 20,
    paddingBottom: 70,
    fontSize: 10,
    fontFamily: "Tajawal",
  },
  arabicText: {
    fontFamily: "Tajawal",
    textAlign: "right",
    direction: "rtl",
  },
  englishText: {
    fontFamily: "Tajawal",
    textAlign: "left",
    direction: "ltr",
  },
  header: {
    textAlign: "center",
    fontSize: 24,
    fontWeight: "bold",
    color: "#79329a",
    marginBottom: 5,
    paddingBottom: 5,
  },
  line: {
    borderBottomWidth: 2,
    borderBottomColor: "#79329a",
    marginBottom: 30,
  },
  dateSection: {
    textAlign: "center",
    fontSize: 12,
    marginBottom: 20,
    color: "#6b7280",
  },
  negativeAmount: {
    color: "#dc2626",
  },
  tableContainer: {
    marginBottom: 20,
  },
  table: {
    flexDirection: "column",
    borderWidth: 1,
    borderColor: "#d1d5db",
    marginBottom: 20,
  },
  tableWrapper: {
    flexDirection: "column",
    borderWidth: 1,
    borderColor: "#d1d5db",
    minPresenceAhead: 100,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#e5e7eb",
    borderBottomWidth: 2,
    borderBottomColor: "#9ca3af",
    fontWeight: "bold",
  },
  tableHeaderRTL: {
    flexDirection: "row-reverse",
    backgroundColor: "#e5e7eb",
    borderBottomWidth: 2,
    borderBottomColor: "#9ca3af",
    fontWeight: "bold",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#d1d5db",
  },
  tableRowRTL: {
    flexDirection: "row-reverse",
    borderBottomWidth: 1,
    borderBottomColor: "#d1d5db",
  },
  tableRowContainer: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#d1d5db",
    orphans: 3,
    widows: 3,
  },
  tableCell: {
    flex: 1,
    padding: 8,
    fontSize: 10,
    textAlign: "center",
    borderRightWidth: 1,
    borderRightColor: "#d1d5db",
  },
  tableCellLast: {
    flex: 1,
    padding: 8,
    fontSize: 10,
    textAlign: "center",
  },
  tableCellType: {
    flexDirection: "column",
    alignContent: "center",
    alignItems: "center",
    gap: 2,
  },
  transactionType: {
    fontSize: 10,
    fontWeight: "bold",
  },
  receiptNumber: {
    fontSize: 8,
    color: "#6b7280",
  },
  summarySection: {
    flexDirection: "row",
    justifyContent: "space-around",
    marginTop: 20,
    gap: 10,
  },
  summaryBox: {
    flex: 1,
    padding: 15,
    borderRadius: 5,
    textAlign: "center",
  },
  balanceBox: {
    backgroundColor: "#ddecf6",
    borderWidth: 1,
    borderColor: "#426f8c",
  },
  boxTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 5,
  },
  boxAmount: {
    fontSize: 16,
    fontWeight: "bold",
  },
  balanceAmount: {
    color: "#dc2626",
  },
  positiveAmount: {
    color: "#16a34a",
  },
  flexRow: {
    display: "flex",
    justifyContent: "space-between",
  },
});
const ExpensesTemplate = ({

  language = "EN",
  companyName = "Your Company Name",
  expenses = [],
  schoolName = "",
}) => {
  const isArabic = language === "AR";

  // Translations
  const translations = {
    EN: {
      kassaName: "Kassa Name",
      currentDate: "Statement Date",
      receiptNumber: "Receipt #",
      date: "Date",
      amount: "Amount",
      balance: "Current Balance",
      schoolName: "School Name",
      expensesReport: "Expenses Report",
      directDeposit: "Direct Deposit",
      directWithdrawal: "Direct Withdrawal",
      studentDept: "Student Payment",
      totalExpenses: "Total Expenses",
      notes: "Notes",
    },
    AR: {
      kassaName: "اسم القاصة",
      schoolName: "إسم المدرسة",
      currentDate: "تاريخ الكشف",
      receiptNumber: "رقم الإيصال",
      date: "التاريخ",
      amount: "المبلغ",
      balance: "الرصيد الحالي",
      alhabbarSlogan: "شركة الحبّار للتطوير البرمجي",
      ehsiblySecondSlogan: "برنامج إحسبلي للإدارة والمحاسبة",
      expensesReport: "كشف المصروفات",
      directDeposit: "إيداع مباشر",
      directWithdrawal: "سحب مباشر",
      studentDept: "دفعة طالب",
      totalExpenses: "إجمالي المصروفات",
      notes: "الملاحظات",
    },
  };

  const t = translations[language] || translations.EN;

  // Calculate summary data
  const summary = {
    balance: expenses.reduce((sum, revenue) => {
      if (revenue.type === "SELL") {
        return sum + Math.abs(revenue.amount);
      } else {
        return sum - Math.abs(revenue.amount);
      }
    }, 0),
    totalExpenses: expenses.length,
  };

  // Get currency (assuming IQD)
  const currency = "IQD";

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US").format(amount) + ` ${currency}`;
  };

  // Format date from ISO string
  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString("en-UK");
  };

  // Get current date
  const getCurrentDate = () => {
    return new Date().toLocaleDateString("en-UK");
  };

  // Helper component for styled text
  const StyledText = ({ children, style = {} }) => {
    const baseStyle = isArabic ? styles.arabicText : styles.englishText;
    return <Text style={{ ...baseStyle, ...style }}>{children}</Text>;
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header - Company Name */}
        <View style={styles.line}>
          <StyledText style={styles.header}>{companyName}</StyledText>
          <StyledText style={styles.dateSection}>{t.revenuesReport}</StyledText>
        </View>

        {/* Current Date */}
        <View style={styles.flexRow}>
          <StyledText style={styles.dateSection}>
            {t.schoolName}: {schoolName}
          </StyledText>
          <StyledText style={styles.dateSection}>
            {t.currentDate}: {getCurrentDate()}
          </StyledText>
        </View>

        {/* Table */}
        <View style={styles.tableContainer}>
          {/* Table Header */}
          <View style={isArabic ? styles.tableHeaderRTL : styles.tableHeader}>
            <StyledText style={styles.tableCell}>{t.kassaName}</StyledText>
            <StyledText style={styles.tableCell}>{t.date}</StyledText>
            <StyledText style={styles.tableCellLast}>{t.amount}</StyledText>
            <StyledText style={styles.tableCellLast}>
              {t.receiptNumber}
            </StyledText>
            <StyledText style={styles.tableCellLast}>{t.notes}</StyledText>
          </View>

          {/* Table Rows */}
          {expenses.map((expense, index) => (
            <View
              key={index}
              style={[
                isArabic ? styles.tableRowRTL : styles.tableRow,
                styles.tableRowContainer,
              ]}
            >
              {isArabic ? (
                <>
                  <StyledText style={styles.tableCell}>
                    {expense.notes || ""}
                  </StyledText>
                  <StyledText style={styles.tableCell}>
                    {expense.receiptNumber || ""}
                  </StyledText>
                  <StyledText
                    style={{
                      ...styles.tableCell,
                      ...styles.negativeAmount,
                    }}
                  >
                    {`-${formatCurrency(expense.amount)}`}
                  </StyledText>
                  <StyledText style={styles.tableCell}>
                    {formatDate(expense.date)}
                  </StyledText>
                  <StyledText style={styles.tableCell}>
                    {expense.kassa.name}
                  </StyledText>
                </>
              ) : (
                <>
                  <StyledText style={styles.tableCell}>
                    {expense.kassa.name}
                  </StyledText>
                  <StyledText style={styles.tableCell}>
                    {formatDate(expense.date)}
                  </StyledText>
                  <StyledText
                    style={{
                      ...styles.tableCell,
                      ...styles.negativeAmount,
                    }}
                  >
                    {`-${formatCurrency(expense.amount)}`}
                  </StyledText>
                  <StyledText style={styles.tableCell}>
                    {expense.receiptNumber || ""}
                  </StyledText>
                  <StyledText style={styles.tableCell}>
                    {expense.notes || ""}
                  </StyledText>
                </>
              )}
            </View>
          ))}
        </View>

        {/* Summary Box */}
        <View style={styles.summarySection} wrap={false}>
          <View style={[styles.summaryBox, styles.balanceBox]}>
            <StyledText style={styles.boxTitle}>{t.balance}</StyledText>
            <StyledText
              style={[
                styles.boxAmount,
                summary.balance < 0
                  ? styles.balanceAmount
                  : styles.positiveAmount,
              ]}
            >
              {formatCurrency(summary.balance)}
            </StyledText>
          </View>
          <View style={[styles.summaryBox, styles.balanceBox]}>
            <StyledText style={styles.boxTitle}>{t.totalExpenses}</StyledText>
            <StyledText style={styles.boxAmount}>
              {summary.totalExpenses}
            </StyledText>
          </View>
        </View>

        {/* Footer Section - Fixed on every page */}
        <View
          fixed
          style={{
            position: "absolute",
            bottom: 20,
            left: 20,
            right: 20,
            textAlign: "center",
            paddingTop: 10,
            borderTopWidth: 1,
            borderTopColor: "#d1d5db",
            flexDirection: isArabic ? "column-reverse" : "column",
            alignItems: "center",
          }}
        >
          <Text
            style={{
              fontSize: 8,
              fontFamily: "Tajawal",
            }}
          >
            {t.alhabbarSlogan}
          </Text>
          <Text
            style={{
              fontSize: 10,
              fontWeight: "bold",
              marginBottom: 4,
              fontFamily: "Tajawal",
            }}
          >
            {t.ehsiblySecondSlogan}
          </Text>
        </View>
      </Page>
    </Document>
  );
};
export default ExpensesTemplate;

